/**
 * New in town: films out recently and exhibitions opening soon.
 *
 * One import, `releaseOn.ts`, which has none.
 */
import { releaseOn, type FilmReleases } from './releaseOn.ts'

/** Days from one ISO date to another, at noon UTC. */
const daysDiff = (a: string, b: string) =>
  Math.round((Date.parse(`${b}T12:00:00Z`) - Date.parse(`${a}T12:00:00Z`)) / 86400000)

interface EventForNew {
  type: string
  film?: FilmReleases | null
  run_start?: string | null
}

/**
 * The date a film or exhibition is new, or null if not.
 *
 * A film is new when its release date *in the current region's country*
 * (`releaseOn`: Germany's `de_release_on` in Berlin, the `release_on` map's
 * entry elsewhere) is within the last 14 days and the next 30 days. An exhibition is new when it opens within the last
 * 7 days and the next 21 days.
 */
export function newOn(e: EventForNew, today: string): string | null {
  const releaseDate = e.type === 'film' ? releaseOn(e.film) : null
  if (releaseDate) {
    const daysUntilRelease = daysDiff(today, releaseDate)
    if (daysUntilRelease >= -14 && daysUntilRelease <= 30) {
      return releaseDate
    }
  }
  if (e.type === 'exhibition' && e.run_start) {
    const startDate = e.run_start
    const daysUntilStart = daysDiff(today, startDate)
    if (daysUntilStart >= -7 && daysUntilStart <= 21) {
      return startDate
    }
  }
  return null
}

/**
 * True if the event is new (for filtering purposes).
 */
export function isNew(e: EventForNew, today: string): boolean {
  return newOn(e, today) !== null
}

interface EventForNewKind {
  type: string
  discipline?: string | null
}

/**
 * Which of the two kinds a row is, from its real `type`/`discipline` split
 * — the same branches `kindOf` (`browse.ts`) and `hueOf` (`rowMeta.ts`)
 * already make, restated here because this file is import-free by rule and
 * `newOn`/`isNew` above only know the words `'film'`/`'exhibition'`, not the
 * schema's own split that produces them. `null` for anything else, opening
 * included as the exhibition it will become.
 *
 * The one place both `Today.tsx` and `browse.ts` read this from, after each
 * wrote its own normaliser the same afternoon without seeing the other's:
 * `Today.tsx`'s treated `opening` as `exhibition`; `browse.ts`'s passed
 * `opening` through untouched, which `newOn`'s own `type === 'exhibition'`
 * check does not match, so an opening row silently never counted as new
 * for `?new=1` while it did on Today. Callers merge the result into the row
 * they hand to `newOn`/`isNew`; a `null` here means "not new-in-town's
 * business" and the caller decides what to pass through instead.
 */
export function newInKind(e: EventForNewKind): 'film' | 'exhibition' | null {
  if (e.type === 'screening' || e.type === 'release' || e.discipline === 'film') return 'film'
  if (e.type === 'exhibition' || e.type === 'opening') return 'exhibition'
  return null
}

/** One row of New in town: the row, which of the two kinds it is, and the
 *  date that makes it new. */
export interface Fresh<E> {
  e: E
  kind: 'film' | 'exhibition'
  on: string
}

/**
 * New in town, newest first — the one list Today's section and Browse's
 * `?new=1` page both draw (a1-16). Each used to run its own copy of this
 * over its own idea of the feed, and "All 77" on Today opened a page headed
 * "New in town 78". The callers still choose the rows they pass in (see
 * each); this decides which of them are new and in what order, once.
 */
export function freshOf<E extends EventForNewKind & Omit<EventForNew, 'type'>>(
  rows: readonly E[], today: string,
): Fresh<E>[] {
  const out: Fresh<E>[] = []
  for (const e of rows) {
    const kind = newInKind(e)
    if (!kind) continue
    const on = newOn({ type: kind, film: e.film, run_start: e.run_start }, today)
    if (on) out.push({ e, kind, on })
  }
  return out.sort((a, b) => (b.on < a.on ? -1 : b.on > a.on ? 1 : 0))
}

/**
 * The films line's date, and the word in front of it (a1-07).
 *
 * `newOn` admits a film up to 30 days before its German release, so the
 * films shown can all be still to come — and "out since Thu 8", read on Fri
 * 2, is a date in the future dressed as one in the past. The earliest of
 * the dates shown is the one the line names; it says "out since" only once
 * that day has come, and "out" before it. Null for no films.
 */
export function filmsOutLabel(
  ons: readonly string[], today: string,
): { word: 'out since' | 'out'; on: string } | null {
  if (ons.length === 0) return null
  const on = ons.reduce((a, b) => (b < a ? b : a))
  return { word: on <= today ? 'out since' : 'out', on }
}
